<template>
    <div class="group-page">
        <div class="alert alert-danger access-error-message" v-if="accessError && initialised">
            Group not found or you do not have access to it.
        </div>

        <div class="group-page__menu-container" v-if="initialised&&!accessError">
            <group-page-menu-top-section
                    v-if="!mobileView"
                    :group="group"
                    :show-buttons-panel="viewer_state.is_member"
                    :show-name="!viewer_state.is_member"
            ></group-page-menu-top-section>
            <group-page-menu-actions
                    v-if="viewer_state.is_member&&!mobileView"
                    @openSettings="openSettings"
                    @startChat="startChat"
                    @leaveGroup="leaveGroup"
            ></group-page-menu-actions>
            <group-page-menu-sharing-panel
                    v-if="viewer_state.is_member&&!mobileView"
                    :group="group"
                    :sharing-state="sharing_state"
            ></group-page-menu-sharing-panel>
            <group-page-menu-members
                    v-if="viewer_state.is_member&&!mobileView"
                    :group="group"
            ></group-page-menu-members>
            <group-page-menu-mobile
                    v-if="mobileView"
                    :group="group"
                    :sharing-state="sharing_state"
                    @openSettings="openSettings"
                    @startChat="startChat"
                    @leaveGroup="leaveGroup"
            ></group-page-menu-mobile>
        </div>

        <div class="group-page__content-container" v-if="initialised&&!accessError">

            <group-page-public-story
                    v-if="initialised&&!viewer_state.is_member"
            ></group-page-public-story>
            <router-view
                    v-if="initialised&&viewer_state.is_member"
            ></router-view>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import GroupPageMenuTopSection from "./navigation_menu/GroupPageMenuTopSection";
    import GroupPageStore from './GroupPageStore';
    import {mapGetters} from "vuex";
    import GroupPagePublicStory from "./public_story/GroupPagePublicStory";
    import GroupPageMenuActions from "./navigation_menu/GroupPageMenuActions";
    import GroupPageMenuSharingPanel from "./navigation_menu/GroupPageMenuSharingPanel";
    import LeftMenuMembersListPreview from "../../../vue/common_components/LeftMenuMembersListPreview";
    import GroupPageMenuMembers from "./navigation_menu/GroupPageMenuMembers";
    import GroupPageHeader from "./common/GroupPageHeader";
    import ScreenWidthAwareMixin from "../../../vue/mixins/ScreenWidthAwareMixin";
    import GroupPageMenuMobile from "./navigation_menu/GroupPageMenuMobile";
    import GroupResource from "../groups/group_resource";
    import GroupSettingsPopup from "./popups/GroupSettingsPopup";
    import {simplePopupFactory} from "shared/simple_popups/simplePopupFactory";
    import GroupChatCreateOrJoinLastPopup from "./popups/GroupChatCreateOrJoinLastPopup";
    import {CurrentUserClass} from "shared/CurrentUser";

    export default {
        name: "GroupPage",
        components: {
            GroupPageMenuMobile,
            GroupPageHeader,
            GroupPageMenuMembers,
            LeftMenuMembersListPreview,
            GroupPageMenuSharingPanel,
            GroupPageMenuActions,
            GroupPagePublicStory,
            GroupPageMenuTopSection
        },
        mixins: [ScreenWidthAwareMixin],
        props: {
            groupSlug: String,
        },
        data() {
            return {};
        },
        computed: {
            ...mapGetters('groupPage', {
                accessError: "accessError",
                initialised: 'initialised',
                group: 'group',
                viewer_state: 'viewer_state',
                sharing_state: 'sharing_state',
            }),
            mobileView() {
                return this.windowWidth < 768;
            },
        },
        methods: {
            showGroupSettingsPopup(groupSettings) {
                const {vm, popupComponent, fEl} = simplePopupFactory.mount_vue_popup(GroupSettingsPopup);
                popupComponent.show(Object.assign({
                    header: 'Edit Group',
                    saveButtonText: 'Save',
                }, groupSettings))
                    .then(updatedGroupData => {
                        this.$store.dispatch('groupPage/updateGroupSettings', updatedGroupData)
                            .then(() => {
                                if (this.group.slug !== this.groupSlug) {
                                    this.$router.replace({...this.$route, params: {groupSlug: this.group.slug}});
                                }
                            });
                    }, () => {
                    })
                    .finally(() => {
                        vm?.$destroy();
                        fEl?.focus();
                    });
            },
            openSettings() {
                const $rootScope = Vue.getAngularModule('$rootScope');
                const $http = Vue.getAngularModule('$http');
                $rootScope.show_dimmer();
                GroupResource($http).get_group_settings(this.group.id)
                    .then(resp => {
                        this.showGroupSettingsPopup(resp.data);
                    }, () => {
                    })
                    .finally(() => {
                        $rootScope.hide_dimmer();
                    });
            },
            showChatCreateOrJoinSelector(lastChat) {
                const $rootScope = Vue.getAngularModule('$rootScope');
                const {vm, popupComponent, fEl} = simplePopupFactory.mount_vue_popup(GroupChatCreateOrJoinLastPopup);
                popupComponent.show(lastChat)
                    .then(res => {
                        if (res.selection === 'create') {
                            this.createNewChat();
                        } else {
                            $rootScope.show_dimmer();
                            document.location.href = this.$options.filters.backHrefQuery(lastChat.link);
                        }
                    })
                    .finally(() => {
                        vm?.$destroy();
                        fEl?.focus();
                    });
            },
            createNewChat() {
                const $rootScope = Vue.getAngularModule('$rootScope');
                const $http = Vue.getAngularModule('$http');
                simplePopupFactory.simple_text_input_popup(
                    'New Chat',
                    'Give this Chat a name?',
                    'Start'
                )
                    .then(name => {
                        $rootScope.show_dimmer();
                        return GroupResource($http).create_conversation(this.group.id, name);
                    }, () => new Promise(() => {
                    }))
                    .then(resp => {
                        document.location.href = this.$options.filters.backHrefQuery(resp.data.link);
                    }, err => {
                        $rootScope.hide_dimmer();
                        Vue.notifications.error(err || 'Error');
                    });
            },
            startChat() {
                const $rootScope = Vue.getAngularModule('$rootScope');
                const $http = Vue.getAngularModule('$http');
                $rootScope.show_dimmer();
                GroupResource($http).get_last_group_conversation(this.group.id)
                    .then(resp => {
                        if (resp.data.status === "OK") {
                            this.showChatCreateOrJoinSelector(resp.data.conversation);
                        } else if (resp.data.status === 'NOT_FOUND') {
                            this.createNewChat();
                        }
                    }, err => {
                        Vue.notifications.error(err || 'Error');
                    })
                    .finally($rootScope.hide_dimmer());
            },
            leaveGroup() {
                const $http = Vue.getAngularModule('$http');
                const CurrentUser = new CurrentUserClass();
                if (this.group.is_buddy_group && (CurrentUser.is_circle_user() || CurrentUser.is_buddy())) {
                    simplePopupFactory.show_popup(
                        'Leave Group',
                        `You can't leave this Group because it is a Buddy Group administrated by your Portal. Please contact your Portal Admin and make the request.`,
                        'OK'
                    );
                    return;
                }
                simplePopupFactory.show_popup(
                    'Leave Group',
                    'Are you sure you want to leave this Group?',
                    'Leave',
                    'Cancel',
                    'btn-red',
                )
                    .then(r => {
                        return GroupResource($http).leave_group(this.group.id);
                    }, () => new Promise(() => {
                    }))
                    .then(resp => {
                        Vue.notifications.success('Left');
                        document.location = resp.data;
                    }, err => Vue.notifications.error(err || "Error"));
            }
        },
        beforeCreate() {
            this.$store.registerModule('groupPage', GroupPageStore);
        },
        mounted() {
            this.$store.commit('groupPage/updateGroup', {slug: this.groupSlug});
            this.$store.dispatch('groupPage/loadInfoAndViewerState')
                .then(loaded => {
                    if (this.viewer_state.is_member) {
                        this.$store.dispatch('groupPage/loadSharingState');
                    }
                }, () => {
                });
        },
        beforeDestroy() {
            this.$store.unregisterModule('groupPage');
        },
        metaInfo() {
            return {
                title: this.group?.name || 'Group'
            };
        }
    };
</script>

<style scoped lang="scss">
  @import "../styles/mixins";

  $left-panel-width-tablet: 270px;
  $left-panel-width: 320px;

  @mixin pageWithLeftMenuMixin() {
    font-size: 0;
    &__menu-container {
      vertical-align: top;
      font-size: $default-font-size;
      @media (min-width: $screen-md-min) {
        width: $left-panel-width;
        display: inline-block;
      }
      @media (min-width: $screen-sm-min) and (max-width: $screen-sm-max) {
        width: $left-panel-width-tablet;
        display: inline-block;
      }
      @media (max-width: $screen-xs-max) {
        width: 100%;
        display: block;
      }
    }

    &__content-container {
      vertical-align: top;
      font-size: $default-font-size;
      @media (min-width: $screen-md-min) {
        width: calc(100% - #{$left-panel-width} - 10px);
        margin-left: 10px;
        display: inline-block;
      }
      @media (min-width: $screen-sm-min) and (max-width: $screen-sm-max) {
        width: calc(100% - #{$left-panel-width-tablet} - 10px);
        margin-left: 10px;
        display: inline-block;
      }
      @media (max-width: $screen-xs-max) {
        width: 100%;
        display: block;
      }
    }

  }

  .group-page {
    @include pageWithLeftMenuMixin;
  }

  .access-error-message {
    font-size: $default-font-size;
  }

</style>
